import { makeAutoObservable, runInAction } from 'mobx';
import { createContext, useContext } from 'react';
import { ProductLineId } from '@insurancemenu/imenu-components-lib/dist/types/ProductLineId';
import { getCart } from 'api/operations/cartServiceV2';
import { getPortfoliosById, overrideRates } from 'api/operations/productServiceV2';
import {
  CartBrowsingCriteria,
  CartItem,
  Portfolio,
  Rate,
} from '@insurancemenu/imenu-components-lib/dist/types/productCardsTypings';
import { toastManager } from '@insurancemenu/imenu-components-lib/dist/components/Toast/toastManager';
import { ToastColorTypes } from '@insurancemenu/imenu-components-lib/dist/types';
import { bpHistory } from 'global/bpHistory';
import { QuotedRatesOverriderDelegate } from './quotedRatesOverriderDelegate';

interface DomainData {
  groupId: string;
  cartId: string;
  periodId: string;
  bcIndex: number;
  portfolioId: string;
  productLineId: ProductLineId;
}

export interface OverrideProduct {
  portfolioId: string;
  bcIndex: number;
  productLineId: ProductLineId;
  productId: string;
  productName: string;
  productSummary: OverrideProductSummary;
}

export interface OverrideProductSummary {
  rates: Rate[];
  browsingCriteria: null | CartBrowsingCriteria;
}

export class OverrideQuotedRatesPageStore {
  private _domainData: DomainData;
  private _isLoading = false;
  private _overrideProducts: OverrideProduct[] = [];
  private _quotedRatesOverriderDelegate: QuotedRatesOverriderDelegate = new QuotedRatesOverriderDelegate({
    quotedProducts: [],
  });

  public get isLoading(): boolean {
    return this._isLoading;
  }

  public get overrideProducts(): OverrideProduct[] {
    return this._overrideProducts;
  }

  public get quotedRatesOverriderDelegate(): QuotedRatesOverriderDelegate {
    return this._quotedRatesOverriderDelegate;
  }

  public get canOverrideRates(): boolean {
    return this.quotedRatesOverriderDelegate.rateForms.every(
      (form) => form.formController.basicFieldsValidationsPassed,
    );
  }

  public get portfolioProductLineId(): ProductLineId {
    return this._domainData.productLineId;
  }

  private _setLoading(loadingStatus: boolean): void {
    this._isLoading = loadingStatus;
  }

  public async getInitialData(): Promise<void> {
    this._setLoading(true);

    const cart = await getCart(this._domainData.cartId);

    await runInAction(async () => {
      const currentCartItem = cart.cartItems.find((item) => item.portfolioId === this._domainData.portfolioId);
      // Passing a single cart item returns only the required single portfolio
      const portfolio = await getPortfoliosById(
        currentCartItem ? [currentCartItem] : [],
        cart.lastUpdateBrowsingCriteria,
        this._domainData.groupId,
        this._domainData.cartId,
        'no-cache',
      );

      runInAction(() => {
        if (!currentCartItem || !portfolio[0]) {
          throw new Error('Current cart item or portfolio not found');
        }

        this._overrideProducts = this._convertProducts(portfolio[0], currentCartItem);
        this._quotedRatesOverriderDelegate.createRateForms(this._overrideProducts);

        this._setLoading(false);
      });
    });
  }

  public async overrideRates(): Promise<void> {
    this._setLoading(true);
    await overrideRates(this._domainData.cartId, this.quotedRatesOverriderDelegate.ratesOverrideData);
    runInAction(() => {
      toastManager.addToast('Rates overridden successfully!', ToastColorTypes.SUCCESS);
      this._setLoading(false);
      this.returnBack();
    });
  }

  private _convertProducts(portfolio: Portfolio, currentCartItem: CartItem): OverrideProduct[] {
    return portfolio.products.map(({ imProductId, productDisplayName, bcDependentData }) => {
      return {
        portfolioId: portfolio.id,
        bcIndex: this._domainData.bcIndex,
        productLineId: portfolio.productLineId,
        productId: imProductId,
        productName: productDisplayName,
        productSummary: {
          rates: bcDependentData[this._domainData.bcIndex].rates,
          browsingCriteria: currentCartItem?.browsingCriteria[this._domainData.bcIndex] || null,
        },
      };
    });
  }

  public returnBack(): void {
    bpHistory.push(
      `/proposal-override-rates/${this._domainData.periodId}?groupId=${this._domainData.groupId}&cartId=${this._domainData.cartId}`,
    );
  }

  constructor(domainData: DomainData) {
    this._domainData = domainData;

    makeAutoObservable(this, {}, { autoBind: true });
  }
}

const OverrideQuotedRatesPageStoreContext = createContext(new OverrideQuotedRatesPageStore({} as DomainData));

const useOverrideQuotedRatesPageStore = (): OverrideQuotedRatesPageStore => {
  return useContext(OverrideQuotedRatesPageStoreContext);
};

export { OverrideQuotedRatesPageStoreContext, useOverrideQuotedRatesPageStore };
